// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import { types as sdkTypes } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  // Check if we're in a browser environment before accessing window
  if (typeof window !== 'undefined') {
    return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
  }

  // For server-side rendering, use the marketplaceRootURL or a default
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : '';
};

// Legacy config shape kept for `src/index.js` createInstance(). The self-hosted
// apiClient ignores it; retained so unrelated call sites don't churn.
export const typeHandlers = [
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// File/Blob/FormData must be sent as-is. JSON.stringify(new File(...)) is "{}"
// (2 bytes), which is what corrupted support-request JPEG downloads.
export const encodeRequestBody = body => {
  if (body == null) return undefined;
  if (typeof body === 'string') return body;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return body;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return body;
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) return body;
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(body)) return body;
  return JSON.stringify(body);
};

// The app is fully self-hosted (`p9-drop-final`): request bodies are JSON and
// responses are JSON parsed through the sdkTypes reviver.
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  const encoded = encodeRequestBody(body);
  const bodyMaybe = encoded === undefined ? {} : { body: encoded };

  const fetchOptions = {
    credentials: credentials || 'include',
    headers: headers || { 'Content-Type': 'application/json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/json') {
      // Parse with the sdkTypes reviver so self-hosted endpoints that emit
      // value tags ({_sdkType:'Money'|'UUID'|...}) rebuild into real instances,
      // so formatMoney/UUID checks throughout the SPA keep working. Plain
      // business JSON is untouched (no tag).
      return res.text().then(t => (t ? JSON.parse(t, sdkTypes.reviver) : {}));
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
export const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// GET method helper
export const get = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
    headers: options.headers || { 'Content-Type': 'application/json' },
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Cart endpoints
export const getCart = () => {
  return request('/api/cart', { method: methods.GET });
};

const sanitizePromoCodeInput = code => {
  if (code == null) return null;
  const s = String(code).trim();
  if (!s) return null;
  return s.slice(0, 80);
};

const sanitizePromoCodesArray = input => {
  if (!Array.isArray(input)) return null;
  const seen = new Set();
  const out = [];
  for (const code of input) {
    const clean = sanitizePromoCodeInput(code);
    if (!clean) continue;
    const key = clean.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(clean);
  }
  return out;
};

export const saveCart = (cart, promoCode = undefined, promoCodes = undefined) => {
  const cartPlain = JSON.parse(JSON.stringify(cart || {}));
  const body = { cart: cartPlain };
  if (promoCode !== undefined) {
    body.promoCode =
      promoCode === null || promoCode === '' ? null : sanitizePromoCodeInput(promoCode);
  }
  if (promoCodes !== undefined) {
    if (promoCodes === null) {
      body.promoCodes = [];
    } else {
      body.promoCodes = sanitizePromoCodesArray(promoCodes) || [];
    }
  }
  return post('/api/cart', body);
};

export const saveCartSession = (cart, promoCodes = undefined) => {
  const cartPlain = JSON.parse(JSON.stringify(cart || {}));
  const body = { cart: cartPlain };
  if (Array.isArray(promoCodes)) {
    body.promoCodes = sanitizePromoCodesArray(promoCodes) || [];
  }
  return post('/api/cart/sync', body);
};

export const getAdminAbandonedCarts = ({
  page = 1,
  perPage = 50,
  since,
  until,
  registeredOnly,
  thresholdHours,
} = {}) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('perPage', String(perPage));
  if (since) params.set('since', since);
  if (until) params.set('until', until);
  if (registeredOnly) params.set('registeredOnly', 'true');
  if (thresholdHours != null) params.set('thresholdHours', String(thresholdHours));
  return get(`/api/admin/abandoned-carts?${params.toString()}`);
};

export const cartLineItems = (items, promoCodeOrCodes) => {
  const body = { items };
  if (Array.isArray(promoCodeOrCodes)) {
    const codes = sanitizePromoCodesArray(promoCodeOrCodes) || [];
    if (codes.length > 0) {
      body.promoCodes = codes;
    }
  } else {
    const single = sanitizePromoCodeInput(promoCodeOrCodes);
    if (single) {
      body.promoCode = single;
    }
  }
  return post('/api/cart-line-items', body);
};

export const getBundles = (idsOrListingId = {}) => {
  const params = new URLSearchParams();
  const { ids, listingId } =
    typeof idsOrListingId === 'object' && !Array.isArray(idsOrListingId)
      ? idsOrListingId
      : { ids: idsOrListingId };

  if (Array.isArray(ids) && ids.length > 0) {
    params.set('ids', ids.filter(Boolean).join(','));
  } else if (typeof ids === 'string' && ids.trim()) {
    params.set('ids', ids.trim());
  }
  if (listingId) {
    params.set('listingId', String(listingId));
  }
  const qs = params.toString();
  return get(`/api/bundles${qs ? `?${qs}` : ''}`);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Initiate a multi-item transaction for items from the same provider.
//
// This endpoint allows creating a single transaction with multiple items
// from the same seller. It combines line items from multiple listings.
//
// See `server/api/initiate-multi-item.js` to see what data should
// be sent in the body.
export const initiateMultiItem = body => {
  return post('/api/initiate-multi-item', body);
};

// Track a guest order by access token (public, no auth).
export const getGuestOrder = token => {
  const params = new URLSearchParams({ token: String(token) });
  return get(`/api/orders/track?${params.toString()}`);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Chat Support endpoint
export const chatSupport = body => {
  // This endpoint expects JSON, not transit
  return request('/api/chat-support', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// Request invoice endpoint
// Allows customers to request an invoice for their order
export const requestInvoice = body => {
  // This endpoint expects JSON, not transit
  return request('/api/request-invoice', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// Generate + return the Stripe invoice PDF URL for the caller's own paid order.
export const downloadInvoice = body => {
  return request('/api/download-invoice', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// Generate + return the Stripe invoice PDF URL for a guest order (token-scoped).
export const downloadGuestInvoice = token => {
  const params = new URLSearchParams({ token: String(token) });
  return get(`/api/orders/invoice?${params.toString()}`);
};

/**
 * Fire the Awin server-to-server conversion pixel.
 * The server reads the HttpOnly `awc` cookie and forwards it to Awin.
 * Fire-and-forget: call without awaiting and swallow errors.
 *
 * @param {{ orderRef: string, amount: string, currency: string, voucherCode?: string }} body
 */
export const awinS2S = body => {
  return request('/api/awin-s2s', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// Notify admin when a user is redirected to an external Stripe Checkout link
export const notifyStripeRedirect = (userEmail, listingTitle, listingId, stripeUrl) => {
  return request('/api/notify-stripe-redirect', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userEmail, listingTitle, listingId, stripeUrl }),
  });
};

// Contact form endpoint
export const submitContactForm = body => {
  // This endpoint expects JSON, not transit
  return request('/api/contact', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// ─── Loyalty Programme ────────────────────────────────────────────────────────

export const getLoyaltyWallet = () => {
  return request('/api/loyalty/wallet', {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const creditLoyaltyPoints = transactionId => {
  return request('/api/loyalty/credit-from-transaction', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transactionId }),
  });
};

export const generateLoyaltyPromoCode = () => {
  return request('/api/loyalty/promo-code', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
};

export const getLoyaltyPromoCodes = () => {
  return request('/api/loyalty/promo-codes', {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const searchSuggestions = ({ q, limit = 8 }) => {
  const params = new URLSearchParams();
  params.set('q', q || '');
  params.set('limit', String(limit));
  return get(`/api/search/suggest?${params.toString()}`);
};

export const fetchBrands = () => get('/api/brands');

export const sendAdminTransactionSkeepers = transactionId =>
  post('/api/admin/transactions/skeepers-send', { transactionId });

// ─── Support requests ("Mes demandes") ────────────────────────────────────────

// List the current user's support requests (or all of them for the Neokasa
// seller / admin). Optional status filter ('open' | 'closed').
export const querySupportRequests = ({ status, page = 1, perPage = 50 } = {}) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('perPage', String(perPage));
  if (status) params.set('status', status);
  return get(`/api/support-requests/query?${params.toString()}`);
};

export const queryMyOrdersForSupport = () => get('/api/support-requests/orders');

// Fetch a single support request with its full message thread + attachments.
export const showSupportRequest = id => {
  const params = new URLSearchParams({ id: String(id) });
  return get(`/api/support-requests/show?${params.toString()}`);
};

// Open a new support request.
// body: { subject, types: string[], description, transactionId?, transactionIds?: string[], attachmentIds?: string[] }
export const createSupportRequest = body => post('/api/support-requests/create', body);

// Post a new message on an existing request.
// body: { requestId, body, attachmentIds?: string[] }
export const sendSupportRequestMessage = body => post('/api/support-requests/messages/send', body);

// Update a request's status ('open' | 'closed').
export const updateSupportRequestStatus = (requestId, status) =>
  post('/api/support-requests/status', { requestId, status });

// Upload one attachment (raw bytes). Returns { data: { id, fileName, ... } }.
// The returned id is passed as `attachmentIds` when creating a request/message.
export const uploadSupportRequestAttachment = file =>
  request('/api/support-requests/attachments/upload', {
    method: methods.POST,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-File-Name': encodeURIComponent(file.name || 'fichier'),
    },
    body: file,
  });

// ─── Newsletter ───────────────────────────────────────────────────────────────

const methodRequest = (method, path, body) =>
  request(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

// Public
export const subscribeNewsletter = ({ email, firstName, source }) =>
  methodRequest('POST', '/api/newsletter/subscribe', { email, firstName, source });

export const newsletterUnsubscribeLookup = token =>
  get(`/api/newsletter/unsubscribe?token=${encodeURIComponent(token || '')}`);

export const newsletterUnsubscribeAct = (token, action) =>
  methodRequest('POST', '/api/newsletter/unsubscribe', { token, action });

// Admin — dashboard / segments / products / preview
export const getNewsletterDashboard = () => get('/api/admin/newsletter/dashboard');
export const getNewsletterSegments = () => get('/api/admin/newsletter/segments');
export const searchNewsletterProducts = (q, { ids } = {}) => {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (Array.isArray(ids) && ids.length) params.set('ids', ids.join(','));
  const qs = params.toString();
  return get(`/api/admin/newsletter/products${qs ? `?${qs}` : ''}`);
};
export const previewNewsletter = body => methodRequest('POST', '/api/admin/newsletter/preview', body);
export const getNewsletterAudience = segment =>
  methodRequest('POST', '/api/admin/newsletter/audience', { segment });

// Admin — subscribers
export const getNewsletterSubscribers = ({ page = 1, perPage = 50, search, status, tag } = {}) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('perPage', String(perPage));
  if (search) params.set('search', search);
  if (status) params.set('status', status);
  if (tag) params.set('tag', tag);
  return get(`/api/admin/newsletter/subscribers?${params.toString()}`);
};
export const createNewsletterSubscriber = body =>
  methodRequest('POST', '/api/admin/newsletter/subscribers', body);
export const updateNewsletterSubscriber = (id, body) =>
  methodRequest('PATCH', `/api/admin/newsletter/subscribers/${encodeURIComponent(id)}`, body);
export const deleteNewsletterSubscriber = id =>
  methodRequest('DELETE', `/api/admin/newsletter/subscribers/${encodeURIComponent(id)}`);

// Admin — campaigns
export const getNewsletterCampaigns = ({ page = 1, perPage = 50, status } = {}) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('perPage', String(perPage));
  if (status) params.set('status', status);
  return get(`/api/admin/newsletter/campaigns?${params.toString()}`);
};
export const createNewsletterCampaign = body =>
  methodRequest('POST', '/api/admin/newsletter/campaigns', body);
export const getNewsletterCampaign = id =>
  get(`/api/admin/newsletter/campaigns/${encodeURIComponent(id)}`);
export const getNewsletterCampaignRecipients = (id, { page = 1, perPage = 50 } = {}) =>
  get(
    `/api/admin/newsletter/campaigns/${encodeURIComponent(id)}/recipients?page=${page}&perPage=${perPage}`
  );
export const updateNewsletterCampaign = (id, body) =>
  methodRequest('PATCH', `/api/admin/newsletter/campaigns/${encodeURIComponent(id)}`, body);
export const deleteNewsletterCampaign = id =>
  methodRequest('DELETE', `/api/admin/newsletter/campaigns/${encodeURIComponent(id)}`);
export const testNewsletterCampaign = (id, emails) =>
  methodRequest('POST', `/api/admin/newsletter/campaigns/${encodeURIComponent(id)}/test`, { emails });
export const sendNewsletterCampaign = id =>
  methodRequest('POST', `/api/admin/newsletter/campaigns/${encodeURIComponent(id)}/send`, {});
export const scheduleNewsletterCampaign = (id, body) =>
  methodRequest('POST', `/api/admin/newsletter/campaigns/${encodeURIComponent(id)}/schedule`, body);

// Public + admin landing page builder
export const getLandingPage = () => get('/api/landing');
export const getAdminLanding = () => get('/api/admin/landing');
export const updateAdminLanding = body => methodRequest('PUT', '/api/admin/landing', body);
export const getAdminLandingRevisions = () => get('/api/admin/landing/revisions');
export const restoreAdminLandingRevision = id =>
  methodRequest('POST', `/api/admin/landing/revisions/${encodeURIComponent(id)}/restore`, {});
export const uploadLandingImage = (arrayBuffer, contentType, fileName) =>
  request('/api/admin/landing/images', {
    method: methods.POST,
    credentials: 'include',
    headers: {
      'Content-Type': contentType || 'application/octet-stream',
      ...(fileName ? { 'x-file-name': fileName } : {}),
    },
    body: arrayBuffer,
  });

// Absolute URL for an authenticated attachment. In local dev this points at
// the API server port; a relative `/api/...` href would hit the SPA instead.
export const supportRequestAttachmentUrl = id =>
  `${apiBaseUrl()}/api/support-requests/attachments/${encodeURIComponent(id)}`;

// Fetch the private S3 object through the API (session cookie) and trigger a
// same-origin blob download. A raw <a href> cannot do this in development
// because the frontend and API run on different ports.
export const downloadSupportRequestAttachment = (id, fileName = 'fichier') => {
  return window
    .fetch(supportRequestAttachmentUrl(id), {
      method: methods.GET,
      credentials: 'include',
    })
    .then(async res => {
      if (res.status >= 400) {
        const data = await res.json().catch(() => ({}));
        const err = new Error(data.message || 'Download failed');
        throw Object.assign(err, data);
      }
      return res.blob();
    })
    .then(blob => {
      // JSON.stringify(File) used to persist "{}" (2 bytes) as the object.
      if (blob.size <= 2) {
        const err = new Error('corrupt_attachment');
        err.error = 'corrupt_attachment';
        throw err;
      }
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName || 'fichier';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    });
};
